import { useDisclosure, useToast } from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IBooking, ILocker, ILockerPricing } from "../types/types";

import Stripe from "stripe";
import { UserContext } from "../App";
import { BookingService } from "../services/booking.service";
import { LockerPricingService } from "../services/locker_pricing.service";
import { UserService } from "../services/user.service";

const lockerPricingService = new LockerPricingService();
const bookingService = new BookingService();
const userService = new UserService();

export const useBooking = () => {
  const { user } = useContext(UserContext);

  const toast = useToast();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const {
    isOpen: isSetupPaymentMethodOpen,
    onOpen: onSetupPaymentMethodOpen,
    onClose: onSetupPaymentMethodClose,
  } = useDisclosure();

  /**
   * LOCKERS DATA
   */
  const [lockerPricing, setLockerPricing] = useState<ILockerPricing[]>();
  const [selectedLockerPricing, setSelectedLockerPricing] = useState<{
    base: number;
    [min_hours: string]: number;
  }>({ base: 0 });
  const [freeLockers, setFreeLockers] = useState<{
    [usage: string]: {
      [size: string]: number;
    };
  }>();
  const [freeLockersLoading, setFreeLockersLoading] = useState<boolean>(false);

  /**
   * BOOKINGS DATA
   */
  const [bookings, setBookings] = useState<IBooking[]>();
  const [bookingsLoading, setBookingsLoading] = useState<boolean>(false);

  /**
   * FORM DATA
   */
  const [usage, setUsage] = useState<ILocker["usage"]>(
    (searchParams.get("usage") as ILocker["usage"]) || undefined
  );
  const [size, setSize] = useState<ILocker["size"]>(
    (searchParams.get("size") as ILocker["size"]) || undefined
  );

  /**
   * PAYMENT METHODS
   */
  const [userPaymentMethods, setUserPaymentMethods] = useState<
    Stripe.PaymentMethod[] | undefined
  >();
  const [userPaymentMethodsLoading, setUserPaymentMethodsLoading] =
    useState<boolean>(false);
  const [chosenPaymentMethods, setChosenPaymentMethods] = useState<
    Stripe.PaymentMethod | undefined
  >();

  const [stripeClientSecret, setStripeClientSecret] = useState<
    string | undefined
  >();

  const getStripeClientSecret = async () => {
    if (user) {
      userService
        .create_setup_intent()
        .then(({ data }) => setStripeClientSecret(data.client_secret))
        .catch((err) => {
          toast({
            title: "Ha ocurrido un error",
            description:
              "Ha ocurrido un error inesperado. Por favor, recarga la página e inténtalo de nuevo",
            status: "error",
            duration: 5000,
            position: "top",
            isClosable: true,
          });
        });
    }
  };

  const onSetupPaymentMethodSuccess = () => {
    onSetupPaymentMethodClose();
    getUserPaymentMethods();
  };

  useEffect(() => {
    getStripeClientSecret();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /**
   * SUBMIT
   */
  const [valid, setValid] = useState<boolean>(false);
  const [startBookingLoading, setStartBookingLoading] =
    useState<boolean>(false);

  /**
   * VALIDATION
   */
  useEffect(() => {
    let is_valid = true;

    if (!usage) {
      is_valid = false;
    }

    if (usage === "luggage" && (size === "S" || !size)) {
      is_valid = false;
    }

    if (!chosenPaymentMethods) {
      is_valid = false;
    }

    if (freeLockers) {
      if (!freeLockers[usage]) {
        is_valid = false;
      }

      if (
        freeLockers[usage] &&
        Object.values(freeLockers[usage] ?? {}).reduce(
          (acc, cur) => acc + cur,
          0
        ) <= 0
      ) {
        is_valid = false;
      }

      if (freeLockers[usage]) {
        if (freeLockers[usage][size] <= 0) {
          is_valid = false;
        }
      }
    } else {
      is_valid = false;
    }

    if (!userPaymentMethods || !chosenPaymentMethods) {
      is_valid = false;
    }

    if (freeLockersLoading || userPaymentMethodsLoading) {
      is_valid = false;
    }

    setValid(is_valid);
  }, [
    usage,
    size,
    freeLockers,
    chosenPaymentMethods,
    userPaymentMethods,
    freeLockersLoading,
    userPaymentMethodsLoading,
  ]);

  /**
   * FORCE SIZES ON CERTAIN USAGES
   */
  useEffect(() => {
    if (usage === "scooter") {
      setSize("L");
    } else if (usage === "belongings") {
      setSize("S");
    }
  }, [usage]);

  /**
   * DATA FETCHING
   */
  useEffect(() => {
    getLockerPricing();
    getBookingAvailability();
    getUserPaymentMethods();
    getBookings();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * CHECK IF USER HAS AN ACTIVE BOOKING
   */
  useEffect(() => {
    if (bookings) {
      const activeBooking = bookings.find((b) => b.status === "active");

      if (activeBooking) {
        toast({
          title: "Reserva activa",
          description: "Tienes una reserva activa",
          status: "info",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
        navigate("/bookings");
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  /**
   * CHOOSE FIRST PAYMENT METHOD
   */
  useEffect(
    () => {
      if (userPaymentMethods) {
        setChosenPaymentMethods(userPaymentMethods[0]);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userPaymentMethods]
  );

  /**
   * LOCKER AVAILABILITY POLLING
   */
  useEffect(() => {
    const interval = setInterval(() => getBookingAvailability(), 10000);

    return () => {
      clearInterval(interval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * SET PRICE
   */
  useEffect(() => {
    if (usage && lockerPricing) {
      if (usage === "luggage" && !size)
        return setSelectedLockerPricing({ base: 0 });

      const price = lockerPricing.filter((lp) => {
        const usage_found = lp.usage === usage;
        const size_found = size ? lp.size === size.toUpperCase() : true;

        return usage_found && size_found;
      });

      if (price.length <= 0) {
        setSelectedLockerPricing({ base: 0 });
      } else {
        const lockersPricing: { [min_hours: string]: number } = {};
        price.forEach(
          (lp) => (lockersPricing[lp.min_hour_duration] = lp.price_per_hour)
        );

        setSelectedLockerPricing({
          ...lockersPricing,
          base: price[0].base_price,
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usage, size, lockerPricing]);

  /**
   * FUNCTIONS
   */
  const getBookings = async () => {
    setBookingsLoading(true);

    bookingService
      .list()
      .then(({ data }) => {
        if (data && data.data) {
          setBookings(data.data);
        }
      })
      .catch((err) => {
        console.log("[getBookingData] err", err);

        toast({
          title: "Error al obtener reservas",
          description: "No se han podido obtener las reservas",
          status: "error",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      })
      .finally(() => {
        setBookingsLoading(false);
      });
  };

  const getLockerPricing = () => {
    lockerPricingService
      .list()
      .then(({ data }) => {
        if (!data || !data.data) return;

        setLockerPricing(data.data);
      })
      .catch((err) => {
        console.log("[getLockerPricing] err", err);

        toast({
          title: "Error al obtener lockers",
          description:
            "No se ha podido obtener la información de precios de los lockers",
          status: "error",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      });
  };

  const getBookingAvailability = async () => {
    setFreeLockersLoading(true);

    try {
      const { data } = await bookingService.availability();

      if (data) {
        setFreeLockers(data);
      }

      setFreeLockersLoading(false);
    } catch (err) {
      setFreeLockersLoading(false);
      console.log("[getBookingPreSetup] err", err);

      toast({
        title: "Error al obtener taquillas disponibles",
        description: "No se han podido obtener las taquillas disponibles",
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    }
  };

  const getUserPaymentMethods = async () => {
    setUserPaymentMethodsLoading(true);

    userService
      .payment_methods()
      .then(({ data }) => setUserPaymentMethods(data.payment_methods))
      .catch((err) => {
        console.error(err);
        toast({
          title: "Error al obtener métodos de pago",
          description: "No se han podido obtener los métodos de pago",
          status: "error",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      })
      .finally(() => {
        setUserPaymentMethodsLoading(false);
      });
  };

  const startBooking = async (booking: {
    usage: ILocker["usage"];
    size: ILocker["size"];
    payment_method: string;
  }) => {
    setStartBookingLoading(true);

    try {
      const { data } = await bookingService.start(booking);

      if (!data) return;

      toast({
        title: "Locker reservado",
        description: "¡Tu locker ha sido reservado con éxito!",
        status: "success",
        duration: 5000,
        position: "top",
        isClosable: true,
      });

      setStartBookingLoading(false);
      navigate("/booking-started?id=" + data.data.id);
    } catch (err) {
      setStartBookingLoading(false);
      console.log("[setupBooking] err", err);

      toast({
        title: "Error al reservar locker",
        description: "No se ha podido reservar el locker",
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    }
  };

  return {
    selectedLockerPricing,
    bookingsLoading,
    startBooking,
    usage,
    setUsage,
    size,
    setSize,
    chosenPaymentMethods,
    setChosenPaymentMethods,
    freeLockers,
    userPaymentMethodsLoading,
    userPaymentMethods,
    stripeClientSecret,
    isSetupPaymentMethodOpen,
    onSetupPaymentMethodOpen,
    onSetupPaymentMethodClose,
    onSetupPaymentMethodSuccess,
    valid,
    startBookingLoading,
  };
};
